import { useSelector } from 'hooks/utils';
import { useDispatch } from 'hooks/utils';

import { Icon } from 'components/UI/Icons';
import { Svgs } from 'environment';
import { selectActiveSort, setActiveSortAction } from 'store/ui/tables';
import { getStoredSorting, setStoredSorting } from 'store/data/entries';
import { TableName } from 'types/index';
import type { ActiveSort, TableSort } from 'store/ui/tables/types';
import { SortingType } from 'store/ui/tables/types';
import { useEffect } from 'react';

export function useTableSort(
	tableName: TableName,
	initialSort: TableSort = null,
	getInitialSortFromLocalStorage?: {
		projectId: string | null;
	}
) {
	const dispatch = useDispatch();

	const projectId = getInitialSortFromLocalStorage?.projectId;

	const activeSort = useSelector(state => selectActiveSort(state.ui.tables, tableName));

	useEffect(() => {
		const isWaitingForProjectId = getInitialSortFromLocalStorage && !projectId;
		if (sortIsInDefaultState(activeSort, initialSort) && !isWaitingForProjectId) {
			const storedSort = projectId ? getStoredSorting(projectId) : null;
			if (storedSort !== null && !equalTableSorts(storedSort, initialSort)) {
				setActiveSort(storedSort);
			} else {
				setActiveSort(initialSort);
			}
		}
	}, [projectId]);

	function setActiveSort(activeSort: TableSort) {
		dispatch(setActiveSortAction({ activeSort, tableName }));
		if (projectId) {
			setStoredSorting(projectId, activeSort);
		}
	}

	function handleSort(columnName: string): TableSort {
		const asc: ActiveSort = {
			column: columnName,
			order: SortingType.Asc
		};
		const desc: ActiveSort = {
			column: columnName,
			order: SortingType.Desc
		};

		if (!activeSort) {
			setActiveSort(asc);
			return asc;
		}

		const { column, order } = activeSort;

		const isSameColumn = column === columnName;
		const isOrderAsc = order === SortingType.Asc;

		if (isSameColumn) {
			const newSort = isOrderAsc ? desc : null;

			setActiveSort(newSort);
			return newSort;
		}

		setActiveSort(asc);
		return asc;
	}

	function getSortIcon(columnName: string, isOrderAsc: boolean) {
		// WORKAROUND UNTIL WE HAVE ARROW UP SVG FROM DESIGN TEAM
		return (
			<div style={{ transform: isOrderAsc ? 'unset' : 'rotate(180deg)' }}>
				<Icon
					svg={Svgs.ArrowDown}
					size={s => s.m}
					active
					onClick={() => handleSort(columnName)}
					marginOffset={{ x: 0.5 }}
				/>
			</div>
		);
	}

	function sortIcon(columnName: string) {
		if (activeSort?.column === columnName) {
			const isOrderAsc = activeSort?.order === SortingType.Asc;

			return getSortIcon(columnName, isOrderAsc);
		}
	}

	return { activeSort, handleSort, sortIcon };
}

export const equalTableSorts = (a: TableSort, b: TableSort) => {
	if (a === null && b === null) return true;
	if (a === null || b === null) return false;
	return a.column === b.column && a.order === b.order;
};

export const sortIsInDefaultState = (activeSort: TableSort | undefined, initialSort: TableSort) => {
	return (
		activeSort === undefined ||
		activeSort === null ||
		equalTableSorts(activeSort ?? null, initialSort)
	);
};
